/**
 * Per-symbol WebSocket pool for ticker price feeds.
 *
 * Pool key: customer_id::symbol_id  (exchange_id excluded intentionally)
 *
 * Traders typically run the same pair across multiple broker accounts. The
 * price difference between brokers for the same pair is negligible, so all
 * cells showing the same pair share ONE WebSocket connection. This bounds
 * connection count to unique (customer, pair) combinations rather than
 * unique (customer, broker, pair) combinations — a significant reduction.
 *
 * The server message format does not include routing metadata, so a single
 * global connection cannot route messages. Per-symbol connections sidestep
 * routing entirely: every message on a connection is for that symbol.
 */

const RECONNECT_BASE_MS = 2000;
const MAX_RECONNECT_DELAY_MS = 30000;

// poolKey → Set<callback>
const subscribers = new Map();

// poolKey → PoolEntry
// { ws, url, customer_id, exchange_id, symbol_id, reconnectAttempts, reconnectTimer, intentionallyClosed }
const pool = new Map();

// ─── Internal: key ─────────────────────────────────────────────────────────────

function poolKey(customer_id, symbol_id) {
    return `${customer_id}::${symbol_id}`;
}

// ─── Internal: send helpers ────────────────────────────────────────────────────

function sendSubscribe(ws, customer_id, exchange_id, symbol_id) {
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ op: "subscribe", customer_id, exchange_id, symbol_id: Number(symbol_id) }));
}

function sendUnsubscribe(ws, customer_id, exchange_id, symbol_id) {
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ op: "unsubscribe", customer_id, exchange_id, symbol_id: Number(symbol_id) }));
}

// ─── Internal: dispatch ────────────────────────────────────────────────────────

function dispatchToSubscribers(key, msg) {
    const cbs = subscribers.get(key);
    if (!cbs) return;
    cbs.forEach((cb) => {
        try { cb(msg); } catch (err) {
            console.error('[TickerWS] subscriber callback error:', err);
        }
    });
}

// ─── Internal: connection lifecycle ───────────────────────────────────────────

function scheduleReconnect(key) {
    const entry = pool.get(key);
    if (!entry || entry.reconnectTimer) return;
    // Exponential back-off: 2s → 4s → 8s … capped at 30s. Retries indefinitely.
    const delay = Math.min(RECONNECT_BASE_MS * Math.pow(2, entry.reconnectAttempts), MAX_RECONNECT_DELAY_MS);
    entry.reconnectAttempts += 1;
    entry.reconnectTimer = setTimeout(() => {
        const e = pool.get(key);
        if (e) e.reconnectTimer = null;
        connectSymbol(key);
    }, delay);
}

function teardownSocket(entry) {
    if (!entry.ws) return;
    entry.ws.onopen = null;
    entry.ws.onmessage = null;
    entry.ws.onerror = null;
    entry.ws.onclose = null;
    if (entry.ws.readyState === WebSocket.OPEN || entry.ws.readyState === WebSocket.CONNECTING) {
        entry.ws.close();
    }
    entry.ws = null;
}

function connectSymbol(key) {
    const entry = pool.get(key);
    if (!entry) return;
    if (entry.ws && (entry.ws.readyState === WebSocket.OPEN || entry.ws.readyState === WebSocket.CONNECTING)) return;

    const ws = new WebSocket(entry.url);
    entry.ws = ws;

    ws.onopen = () => {
        if (pool.get(key)?.ws !== ws) return;
        entry.reconnectAttempts = 0;
        sendSubscribe(ws, entry.customer_id, entry.exchange_id, entry.symbol_id);
    };

    ws.onmessage = (ev) => {
        if (pool.get(key)?.ws !== ws) return;
        let msg;
        try { msg = JSON.parse(ev.data); } catch { return; }
        dispatchToSubscribers(key, msg);
    };

    ws.onerror = (err) => {
        console.error('[TickerWS] error for', key, err);
    };

    ws.onclose = () => {
        if (pool.get(key)?.ws !== ws) return;
        entry.ws = null;
        if (!entry.intentionallyClosed && subscribers.has(key)) {
            scheduleReconnect(key);
        }
    };
}

function disconnectIfIdle(key) {
    if (subscribers.has(key)) return;
    const entry = pool.get(key);
    if (!entry) return;
    entry.intentionallyClosed = true;
    if (entry.reconnectTimer) {
        clearTimeout(entry.reconnectTimer);
        entry.reconnectTimer = null;
    }
    sendUnsubscribe(entry.ws, entry.customer_id, entry.exchange_id, entry.symbol_id);
    teardownSocket(entry);
    pool.delete(key);
}

// ─── Public API ────────────────────────────────────────────────────────────────

/**
 * Subscribe to price updates for a symbol.
 *
 * Multiple cells with the same customer_id + symbol_id (even across different
 * exchange_ids) share one WebSocket connection. The exchange_id of the first
 * subscriber is used for the server subscribe message.
 *
 * @param {string}          customer_id
 * @param {string}          exchange_id   Used only for the first subscriber per symbol.
 * @param {string|number}   symbol_id     Pair name or numeric ID (e.g. "EURUSD" or "23").
 * @param {function}        callback      Called with each parsed price message.
 * @returns {function}                    Unsubscribe; safe to call multiple times.
 */
export function subscribeToTicker(customer_id, exchange_id, symbol_id, callback) {
    const url = process.env.REACT_APP_WEBSOCKET_TICKER;
    if (!url) {
        console.error('[TickerWS] REACT_APP_WEBSOCKET_TICKER env var is not set');
        return () => { };
    }

    const key = poolKey(customer_id, symbol_id);

    if (!subscribers.has(key)) subscribers.set(key, new Set());
    subscribers.get(key).add(callback);

    if (!pool.has(key)) {
        pool.set(key, {
            ws: null, url, customer_id, exchange_id, symbol_id,
            reconnectAttempts: 0, reconnectTimer: null, intentionallyClosed: false,
        });
        connectSymbol(key);
    } else {
        const entry = pool.get(key);
        entry.intentionallyClosed = false;
        if (!entry.ws || entry.ws.readyState === WebSocket.CLOSED) {
            connectSymbol(key);
        }
        // If OPEN or CONNECTING: server is already streaming this symbol — no action needed.
    }

    let unsubscribed = false;
    return function unsubscribe() {
        if (unsubscribed) return;
        unsubscribed = true;
        const set = subscribers.get(key);
        if (set) {
            set.delete(callback);
            if (set.size === 0) subscribers.delete(key);
        }
        disconnectIfIdle(key);
    };
}

/**
 * Returns true when the WebSocket for this symbol is currently open.
 * exchange_id is accepted for API symmetry but not used for the pool lookup.
 */
export function isTickerConnected(customer_id, exchange_id, symbol_id) {
    const entry = pool.get(poolKey(customer_id, symbol_id));
    return entry?.ws?.readyState === WebSocket.OPEN;
}
